import React from 'react'
import { CardsContainer, OfferCard } from '@labrav/react-components'
import {
  BuyerDetailRow,
  BuyerInfo,
} from '../../../common/components/DetailRow/DetailRow'

import { useTheme } from '@mui/material/styles'
import { useStyles } from './Revenues.styles'

import { DateTime } from 'luxon'
import {
  standardLabraDateFormat,
  numberFormat,
  ShortenedNameV2,
} from '@labrav/common'
import { CustomLegends } from '../../FlyoutOverview/CustomLegends/CustomLegends'
import { LinearProgress } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from '../../../store/hooks'

interface RevenueCardProps {
  offerName: string
  productName: string
  lastInvoiceDate: string
  lastDisbursedReceivedOn: string | null
  lastDisbursedAmount: number | null
  paymentTerms: string
  nextPaymentDueDate: string
  totalDisbursed: number
  pendingAmount: number
  subscriptionId: string
}

const RevenueCard: React.FC<RevenueCardProps> = ({
  offerName,
  productName,
  lastInvoiceDate,
  lastDisbursedReceivedOn,
  lastDisbursedAmount,
  paymentTerms,
  nextPaymentDueDate,
  totalDisbursed,
  pendingAmount,
  subscriptionId,
}) => {
  const theme = useTheme()
  const { classes } = useStyles()
  const timeZone = useSelector<string>(
    state => state.userProfile.userProfile?.timeZone || 'UTC'
  )
  const navigate = useNavigate()
  const { accountId } = useParams<{
    accountId?: string
  }>()

  const progress = totalDisbursed
    ? (totalDisbursed / (totalDisbursed + pendingAmount)) * 100
    : 0

  const goToSubscriptionDetail = () => {
    const subscriptionsPath = accountId
      ? `/flyout/aws/${accountId}/subscriptions/${subscriptionId}/detail`
      : `/flyout/subscriptions/${subscriptionId}/detail`
    navigate(subscriptionsPath)
  }

  return (
    <CardsContainer hoverEffect={true} handleClick={goToSubscriptionDetail}>
      <OfferCard
        name={
          <div className={classes.name}>
            <ShortenedNameV2 name={offerName} length={40}></ShortenedNameV2>
            <div className={classes.product}>{productName}</div>
          </div>
        }
        rightSideSection={
          <div
            className={classes.graph}
            data-testid={'channel-partners-line-chart'}
          >
            <LinearProgress
              variant="determinate"
              value={progress}
              className={classes.graph}
              classes={{
                colorPrimary: classes.colorPrimary,
                barColorPrimary: classes.barColorPrimary,
              }}
            />
            <div
              className={classes.legendsWrapper}
              data-testid={'channel-partners-custom-legend-wrapper'}
            >
              <CustomLegends
                legends={[
                  {
                    label: 'Recieved:',
                    legendColor: theme.palette.percentage.main,
                    legendSize: 'small',
                    value: (
                      <>
                        <span className={classes.currency}>$</span>
                        {String(numberFormat(totalDisbursed))}
                      </>
                    ),
                  },
                  {
                    label: 'Pending:',
                    legendColor: theme.palette.chips.light,
                    legendSize: 'small',
                    value: (
                      <>
                        <span className={classes.currency}>$</span>
                        {String(numberFormat(pendingAmount))}
                      </>
                    ),
                  },
                ]}
              />
            </div>
          </div>
        }
        isLastCard={true}
        customiseForBuyer={true}
        hideLogo={true}
        onArrowClick={goToSubscriptionDetail}
      />

      <BuyerDetailRow
        children={
          <>
            <BuyerInfo
              title={'Last invoice date'}
              value={
                <div>
                  {lastInvoiceDate
                    ? DateTime.fromISO(lastInvoiceDate)
                        .setZone(timeZone)
                        .toFormat(standardLabraDateFormat)
                    : '-'}
                </div>
              }
              gridSize={2}
            />
            <BuyerInfo
              title={'Last disbursement received on'}
              value={
                <div>
                  {lastDisbursedReceivedOn
                    ? DateTime.fromISO(lastDisbursedReceivedOn)
                        .setZone(timeZone)
                        .toFormat(standardLabraDateFormat)
                    : '-'}
                </div>
              }
              gridSize={2}
            />
            <BuyerInfo
              title={'Last disbursement amount'}
              value={
                <div>
                  {lastDisbursedAmount !== null ? (
                    <>
                      <span className={classes.currency}>$</span>
                      {numberFormat(lastDisbursedAmount, 2)}
                    </>
                  ) : (
                    '-'
                  )}
                </div>
              }
              gridSize={2}
            />
            <BuyerInfo
              title={'Payment terms'}
              value={<div>Net {paymentTerms}</div>}
              gridSize={2}
            />
            <BuyerInfo
              title={'Next payment due date'}
              value={
                <div>
                  {nextPaymentDueDate
                    ? DateTime.fromISO(nextPaymentDueDate)
                        .setZone(timeZone)
                        .toFormat(standardLabraDateFormat)
                    : '-'}
                </div>
              }
              gridSize={2}
              showDivider={false}
            />
          </>
        }
      />
    </CardsContainer>
  )
}

export default RevenueCard
